import { useLayoutEffect, useMemo } from "react";
import {
  applyCoachColour,
  isUsableCoachColour,
  normaliseCoachHex,
} from "./coach-colour";

/**
 * The hook a page uses to hand the action over to the coach.
 *
 * `useLayoutEffect` and not `useEffect`: the variables have to be on the root BEFORE anything
 * paints, and before Stripe reads them. `src/stripe/appearance.ts` resolves every colour it gives
 * Stripe with `getComputedStyle` at the moment the Elements provider mounts, so a colour applied
 * one frame late would leave the card fields on Gymkee orange until something remounted them.
 * The pages also key their Elements provider on the resolved hex, which is the belt to that brace.
 *
 * It returns the resolved hex so a caller can key on it, and it takes the colour back on unmount.
 */
export function useCoachColour(hex?: string | null): string | null {
  const resolved = useMemo(() => resolveCoachHex(hex), [hex]);

  useLayoutEffect(() => {
    applyCoachColour(resolved);
    return () => {
      applyCoachColour(null);
    };
  }, [resolved]);

  return resolved;
}

/**
 * The coach's colour, or the one a `?coach=` in the URL asks for.
 *
 * The override exists so several colours can be photographed on one real payload — a pale one
 * where the gate must put ink on the fill, a dark one where it must put paper. It is compiled out
 * of a build: `import.meta.env.DEV` is a constant at build time, so the branch and the query read
 * are gone from production entirely.
 */
export function resolveCoachHex(hex?: string | null): string | null {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = normaliseCoachHex(
      new URLSearchParams(window.location.search).get("coach"),
    );
    if (override) return usable(override);
  }
  return usable(normaliseCoachHex(hex));
}

/**
 * A colour the gate refuses is not this page's colour, so the Elements key must not carry it
 * either. `applyCoachColour` writes nothing for it; returning null here keeps the key, and every
 * caller that asks "which colour is this page on", on the same answer.
 */
function usable(hex: string | null): string | null {
  return hex !== null && isUsableCoachColour(hex) ? hex : null;
}
